import { useEffect, useRef } from 'react';
import { useToast } from '@/contexts/GlobalProvider';

const SAME_ERROR_THROTTLE_MS = 15000;
const DEFAULT_ERROR_MESSAGE = '页面暂时无法加载，请刷新后重试';

function getRejectionMessage(reason: unknown): string {
  if (reason instanceof Error) {
    return reason.message || DEFAULT_ERROR_MESSAGE;
  }
  if (typeof reason === 'string') {
    return reason || DEFAULT_ERROR_MESSAGE;
  }
  if (reason && typeof reason === 'object' && 'message' in reason) {
    return String((reason as { message?: unknown }).message || DEFAULT_ERROR_MESSAGE);
  }
  return DEFAULT_ERROR_MESSAGE;
}

/**
 * 全局未处理错误处理器
 * 监听未被 catch 的 Promise rejection，并显示 Toast 提示
 */
export function useGlobalErrorHandler() {
  const toast = useToast();
  const toastRef = useRef(toast);
  const lastToastRef = useRef<{ message: string; time: number } | null>(null);

  // 保持 ref 同步
  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  useEffect(() => {
    // SSR 守卫
    if (typeof window === 'undefined') return;

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('[GlobalErrorHandler] 未处理的 Promise 异常:', event.reason);

      // 提取错误消息
      const message = getRejectionMessage(event.reason);
      const now = Date.now();
      const lastToast = lastToastRef.current;

      if (
        lastToast &&
        lastToast.message === message &&
        now - lastToast.time < SAME_ERROR_THROTTLE_MS
      ) {
        event.preventDefault();
        return;
      }

      lastToastRef.current = { message, time: now };
      toastRef.current.error(message);
    };

    const handleError = (event: ErrorEvent) => {
      console.error('[GlobalErrorHandler] 未处理的运行时异常:', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
      });
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []); // 空依赖数组，只在挂载时注册一次
}
